import { Pressable } from "react-native";
import { usePathname, useRouter, useLocalSearchParams } from "expo-router";
import type { Href } from "expo-router";
import { StyleSheet, useUnistyles } from "react-native-unistyles";
import { View, Text } from "@src/components";
import { Icon } from "@src/components/Icon";
import { sizes } from "@src/constants/sizes";
import { useTenant, getTenantDisplayName } from "@src/features/tenants";
import { Logo } from "./Logo.web";
import { SidepanelCameraList } from "./SidepanelCameraList.web";
import { SidepanelScenarioList } from "./SidepanelScenarioList.web";
import { SidepanelActionItem } from "./SidepanelActionItem.web";
import { SidepanelAnimatedActionItem } from "./SidepanelAnimatedActionItem";
import { useSelectedSiteStore } from "@src/stores/selected-site-store";
import { useCallback, useEffect, useMemo, useState } from "react";
import { groupCamerasBySite } from "@src/features/cameras/utils/camera-group";
import { useCameras } from "@src/features/cameras";
import { SiteDropdown } from "./SiteDropdown.web";
import { useRegionConfig } from "@src/hooks/useRegionConfig";
import { FeatureGate } from "@src/services/feature-flags/FeatureGate";
import { FEATURE_FLAGS, useBooleanFlag } from "@src/services/feature-flags/feature-flags";

interface SidepanelProps {
  collapsed: boolean;
  onToggleCollapse: () => void;
  onOpenTenantSwitcher?: () => void;
}

export function Sidepanel({ collapsed, onToggleCollapse, onOpenTenantSwitcher }: SidepanelProps) {
  const router = useRouter();
  const pathname = usePathname();
  // expo-router exposes the active route's dynamic segments here. On a
  // /tenants/[tenantId]/sites/[siteId]/traffic-flow URL `siteId` is set;
  // on the bare /tenants/[tenantId]/traffic-flow URL it's undefined.
  const { selectedScenario, siteId: routeSiteId } = useLocalSearchParams<{
    selectedScenario?: string;
    siteId?: string;
  }>();
  const { theme } = useUnistyles();
  const { selectedTenant } = useTenant();
  const { data: cameras } = useCameras();
  const { contactUrl } = useRegionConfig();
  const tenantName = selectedTenant ? getTenantDisplayName(selectedTenant) : "";
  const storeSiteId = useSelectedSiteStore((s) => s.selectedSiteId);
  const setSelectedSiteId = useSelectedSiteStore((s) => s.setSelectedSiteId);

  // On a traffic-flow page, the URL is the source of truth — `routeSiteId`
  // is the site segment when present, or null on the bare /traffic-flow URL
  // (= explicit "All sites"). On any other page, fall back to the persisted
  // store so the dropdown remembers the user's last selection.
  const isOnTrafficFlow = pathname.includes("/traffic-flow");
  const selectedSiteId = isOnTrafficFlow ? (routeSiteId ?? null) : storeSiteId;

  // Reconcile the persisted store with the URL so other tabs (Cameras) pick
  // up the current site after the user navigates away from a traffic-flow
  // page. The render-time `selectedSiteId` above already reflects the URL,
  // so this effect is purely for cross-tab persistence.
  useEffect(() => {
    if (isOnTrafficFlow) {
      const next = routeSiteId ?? null;
      if (next !== storeSiteId) setSelectedSiteId(next);
    }
  }, [isOnTrafficFlow, routeSiteId, storeSiteId, setSelectedSiteId]);

  const siteGroups = useMemo(() => {
    if (!cameras) return [];
    return groupCamerasBySite(cameras, "site");
  }, [cameras]);

  const selectedSiteName = useMemo(() => {
    if (!selectedSiteId) return null;
    return siteGroups.find((g) => g.siteId === selectedSiteId)?.siteName ?? null;
  }, [selectedSiteId, siteGroups]);

  const isReportsSelected = pathname === "/reports" || pathname.startsWith("/reports/");
  const isSettingsSelected = pathname === "/settings" || pathname.startsWith("/settings/");

  const [camerasSectionExpanded, setCamerasSectionExpanded] = useState(true);
  const [trafficFlowSectionExpanded, setTrafficFlowSectionExpanded] = useState(true);
  const toggleCamerasSection = useCallback(() => setCamerasSectionExpanded((prev) => !prev), []);
  const toggleTrafficFlowSection = useCallback(() => setTrafficFlowSectionExpanded((prev) => !prev), []);
  const isTrafficFlowEnabled = useBooleanFlag(FEATURE_FLAGS.TRAFFIC_FLOW_TAB);

  const handleAlarmSettings = () => {
    if (selectedTenant?.id) {
      router.push(`/tenants/${selectedTenant.id}/alarm-settings` as never);
    }
  };

  const handleLogout = () => {
    router.push("/auth/logout");
  };

  const handleHelp = () => {
    // Handled via link semantics when href/externalHref is present
  };

  const handleSettings = () => {
    router.push("/settings");
  };

  const handleReports = () => {
    router.push("/reports" as never);
  };

  const handleLogoPress = () => {
    router.push("/");
  };

  // Collapsed state
  if (collapsed) {
    return (
      <View
        width={150}
        bg="surfaceCard"
        borderRadius={sizes.radius}
        p="6xl"
        gap="xl"
        borderWidth={1}
        borderLeftWidth={0}
        borderColor="borderCard"
        shadow="xl"
      >
        {/* Logo */}
        <View alignItems="center">
          <Pressable onPress={handleLogoPress} accessibilityRole="button">
            <Logo collapsed />
          </Pressable>
        </View>

        {/* Divider */}
        <View py="xs">
          <View height={1} bg="borderCard" />
        </View>

        {/* Expand button */}
        <View alignItems="center">
          <Pressable onPress={onToggleCollapse} style={styles.collapseToggle}>
            <Icon name="chevronRight" size={theme.spacing["5xl"]} color={theme.colors.textMuted} />
          </Pressable>
        </View>

        {/* Camera List — fills remaining space */}
        <View flex={1}>
          <View flexDirection="row" justifyContent="space-between" alignItems="center" py="xl">
            <View flexDirection="row" alignItems="center" gap="xs" justifyContent="center" style={{ width: "100%" }}>
              <Icon name="videoOutline" size={theme.spacing["5xl"]} color={theme.colors.textSecondary} />
            </View>
          </View>
          <SidepanelCameraList collapsed selectedSiteId={selectedSiteId} />
        </View>

        {/* Traffic Flow — icon-only when sidebar collapsed, mirrors Cameras header style */}
        {isTrafficFlowEnabled && (
          <Pressable
            onPress={() => {
              if (!selectedTenant?.id) return;
              const path =
                selectedSiteId ?
                  `/tenants/${selectedTenant.id}/sites/${selectedSiteId}/traffic-flow`
                : `/tenants/${selectedTenant.id}/traffic-flow`;
              router.push(path as never);
            }}
            accessibilityRole="button"
            accessibilityLabel="Traffic Flow"
          >
            <View flexDirection="row" alignItems="center" gap="xs" justifyContent="center" py="xl">
              <Icon name="motion" size={theme.spacing["5xl"]} color={theme.colors.textSecondary} />
            </View>
          </Pressable>
        )}

        <View gap="lg">
          <FeatureGate flag={FEATURE_FLAGS.SITE_WIDE_CONTROLS}>
            <SidepanelAnimatedActionItem
              label="Alarm Settings"
              icon="settingsCamera"
              onPress={handleAlarmSettings}
              isSelected={pathname.includes("/alarm-settings") && !pathname.includes("/cameras/")}
              collapsed
            />
          </FeatureGate>
        </View>

        {/* Divider */}
        <View py="xs">
          <View height={1} bg="borderCard" />
        </View>

        {/* Bottom actions */}
        <View gap="lg">
          <FeatureGate flag={FEATURE_FLAGS.REPORTS}>
            <SidepanelAnimatedActionItem
              label="Reports"
              icon="reportDocument"
              onPress={handleReports}
              href={"/reports" as Href}
              isSelected={isReportsSelected}
              collapsed
            />
          </FeatureGate>
          <SidepanelAnimatedActionItem
            label="Settings"
            icon="settingsOutline"
            onPress={handleSettings}
            href={"/settings"}
            isSelected={isSettingsSelected}
            collapsed
          />
          <SidepanelActionItem label="Help" icon="help" onPress={handleHelp} externalHref={contactUrl} collapsed />
          <SidepanelActionItem label="Log out" icon="logout" onPress={handleLogout} collapsed colorToken="danger" />
        </View>
      </View>
    );
  }

  // Expanded state
  return (
    <View
      width={sizes.webSidebarWidth}
      bg="surfaceCard"
      borderRadius={sizes.radius}
      p="6xl"
      gap="xl"
      borderWidth={1}
      borderLeftWidth={0}
      borderColor="borderCard"
      shadow="xl"
    >
      {/* Logo + separator */}
      <View gap="2xl">
        {/* Top row with logo and collapse button */}
        <View flexDirection="row" justifyContent="space-between" alignItems="flex-start" height={36}>
          <Pressable onPress={handleLogoPress} accessibilityRole="button">
            <Logo />
          </Pressable>
          <Pressable onPress={onToggleCollapse} style={styles.collapseToggle}>
            <Icon name="chevronLeft" size={theme.spacing["5xl"]} color={theme.colors.textMuted} />
          </Pressable>
        </View>

        {/* Separator */}
        <View py="xs">
          <View height={1} bg="borderCard" />
        </View>
      </View>

      <SiteDropdown
        siteGroups={siteGroups}
        selectedSiteId={selectedSiteId}
        selectedSiteName={selectedSiteName}
        onSelectSite={(siteId) => {
          setSelectedSiteId(siteId);
          // If we're on a traffic-flow page, replace the site segment so
          // the URL stays in sync with the dropdown. Preserve the
          // ?selectedScenario= query so the detail panel stays open
          // across site switches — the screen looks up the scenario
          // across all sites and renders the panel even if the
          // dropdown filter no longer includes it.
          const isOnTrafficFlow = pathname.includes("/traffic-flow");
          if (isOnTrafficFlow && selectedTenant?.id) {
            const base =
              siteId ?
                `/tenants/${selectedTenant.id}/sites/${siteId}/traffic-flow`
              : `/tenants/${selectedTenant.id}/traffic-flow`;
            const target = selectedScenario ? `${base}?selectedScenario=${selectedScenario}` : base;
            router.replace(target as never);
          }
        }}
      />

      {/* Camera List — fills remaining space when expanded; shrinks to header when collapsed */}
      <View style={camerasSectionExpanded ? { flex: 1, minHeight: 0, overflow: "hidden" } : { flex: 0, flexShrink: 0 }}>
        <Pressable
          onPress={toggleCamerasSection}
          accessibilityRole="button"
          accessibilityLabel={camerasSectionExpanded ? "Collapse cameras section" : "Expand cameras section"}
          testID="cameras-section-toggle"
        >
          <View flexDirection="row" justifyContent="space-between" alignItems="center" py="xl">
            <View flexDirection="row" alignItems="center" gap="xs">
              <Icon name="videoOutline" size={theme.spacing["5xl"]} color={theme.colors.textSecondary} />
              <Text textStyle="sm/regular" color="textSecondary">
                Cameras
              </Text>
            </View>
            <Icon
              name={camerasSectionExpanded ? "chevronUp" : "chevronDown"}
              size={theme.spacing["5xl"]}
              color={theme.colors.textSecondary}
            />
          </View>
        </Pressable>
        {camerasSectionExpanded && <SidepanelCameraList selectedSiteId={selectedSiteId} />}
      </View>

      {/* Divider between Cameras and Traffic Flow sections */}
      {isTrafficFlowEnabled && (
        <View py="xs">
          <View height={1} bg="borderCard" />
        </View>
      )}

      {/* Traffic Flow section — same collapsible header pattern as Cameras */}
      {isTrafficFlowEnabled && (
        <View
          style={
            trafficFlowSectionExpanded ? { flex: 1, minHeight: 0, overflow: "hidden" } : { flex: 0, flexShrink: 0 }
          }
        >
          <Pressable
            onPress={toggleTrafficFlowSection}
            accessibilityRole="button"
            accessibilityLabel={
              trafficFlowSectionExpanded ? "Collapse traffic flow section" : "Expand traffic flow section"
            }
            testID="traffic-flow-section-toggle"
          >
            <View flexDirection="row" justifyContent="space-between" alignItems="center" py="xl">
              <View flexDirection="row" alignItems="center" gap="xs">
                <Icon name="motion" size={theme.spacing["5xl"]} color={theme.colors.textSecondary} />
                <Text textStyle="sm/regular" color="textSecondary">
                  Traffic Flow
                </Text>
              </View>
              <Icon
                name={trafficFlowSectionExpanded ? "chevronUp" : "chevronDown"}
                size={theme.spacing["5xl"]}
                color={theme.colors.textSecondary}
              />
            </View>
          </Pressable>
          {trafficFlowSectionExpanded && <SidepanelScenarioList selectedSiteId={selectedSiteId} />}
        </View>
      )}

      <View gap="lg" style={{ marginTop: "auto" }}>
        <FeatureGate flag={FEATURE_FLAGS.SITE_WIDE_CONTROLS}>
          <SidepanelAnimatedActionItem
            label="Alarm Settings"
            icon="settingsCamera"
            onPress={handleAlarmSettings}
            isSelected={pathname.includes("/alarm-settings") && !pathname.includes("/cameras/")}
          />
        </FeatureGate>
      </View>

      {/* Bottom Section */}
      <View gap="xl">
        {/* Separator */}
        <View py="xs">
          <View height={1} bg="borderCard" />
        </View>

        {onOpenTenantSwitcher && (
          <Pressable
            onPress={onOpenTenantSwitcher}
            accessibilityRole="button"
            accessibilityLabel="Switch organisation"
            testID="tenant-switcher-trigger"
            style={styles.tenantSwitcher}
          >
            <Icon name="swapVertical" size={theme.spacing["5xl"]} color={theme.colors.icon} />
            <View flex={1}>
              <Text textStyle="xs/regular" color="textMuted" numberOfLines={1}>
                Organisation
              </Text>
              <Text textStyle="sm/regular" color="text" numberOfLines={1}>
                {tenantName}
              </Text>
            </View>
            <Icon name="chevronRight" size={16} color={theme.colors.textMuted} />
          </Pressable>
        )}

        {/* Navigation items */}
        <View gap="lg">
          <FeatureGate flag={FEATURE_FLAGS.REPORTS}>
            <SidepanelAnimatedActionItem
              label="Reports"
              icon="reportDocument"
              onPress={handleReports}
              href={"/reports" as Href}
              isSelected={isReportsSelected}
            />
          </FeatureGate>
          <SidepanelAnimatedActionItem
            label="Settings"
            icon="settingsOutline"
            onPress={handleSettings}
            href={"/settings"}
            isSelected={isSettingsSelected}
          />
          <SidepanelActionItem label="Help" icon="help" onPress={handleHelp} externalHref={contactUrl} />
          <SidepanelActionItem label="Log out" icon="logout" onPress={handleLogout} colorToken="danger" />
        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create((theme) => ({
  collapseToggle: {
    width: 36,
    minWidth: 36,
    height: 36,
    borderRadius: theme.spacing.md,
    borderWidth: 1,
    borderColor: theme.colors.borderCard,
    backgroundColor: theme.colors.surfaceCard,
    alignItems: "center",
    justifyContent: "center",
  },
  tenantSwitcher: {
    flexDirection: "row",
    alignItems: "center",
    gap: theme.spacing.md,
    paddingVertical: theme.spacing.lg,
    paddingHorizontal: theme.spacing.xl,
    borderRadius: theme.spacing.md,
  },
}));
